import { useEffect, useMemo, useState } from "react";

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const slides = useMemo(
    () => [
      {
        title: "Approval Workflow",
        description:
          "Track requests, approvals, and rejected documents in one place, giving teams clear visibility into every approval process.",
        image:
          "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/909886e5cfaa593ebd25a51ad30460b1.png",
      },
      {
        title: "Asset Operations",
        description:
          "Manage asset requests, allocation history, and operational status in a structured system to reduce repetitive administrative tasks.",
        image:
          "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/7947171a88d6a179701b61814481d4b1.png",
      },
      {
        title: "Fleet & Facility",
        description:
          "Handle vehicle, facility, and operational requests in a single platform for more efficient and centralized management.",
        image:
          "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/0397be325c2e938751a64c2aa66b8925.png",
      },
    ],
    []
  );

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20corporate%20glass%20office%20building%20interior%20bright%20open%20plan%20workspace%20white%20desks%20panoramic%20floor%20to%20ceiling%20windows%20city%20view%20professional%20enterprise%20environment%20clean%20contemporary%20minimal%20design&width=1920&height=1080"
          className="w-full h-full object-cover"
        />

        {/* ✅ 어두운 오버레이 유지 */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">

          {/* Left */}
          <div
            className={`transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-white text-xs">
                Used by 100+ companies worldwide
              </span>
            </div>

            <h1 className="text-5xl font-bold text-white leading-tight mb-6">
              Smart Workplace
              <br />
              <span className="text-blue-400">Management,</span>
              <br />
              All in One Platform
            </h1>

            <p className="text-lg text-white/80 mb-8 max-w-lg">
              Streamline approvals, manage assets, and optimize operations with
              BizLab.
            </p>

            {/* ✅ 버튼 기능 복구 */}
            <div className="flex gap-4">
              <button
                onClick={() => window.open("http://bizlab-demo.4-lab.com", "_blank")}
                className="px-7 py-3 bg-blue-400 hover:bg-blue-500 text-white rounded-full transition-all duration-200"
              >
                Request Demo
              </button>

              <button
                onClick={() => handleScroll("#features")}
                className="px-7 py-3 border border-white text-white rounded-full"
              >
                Explore Features
              </button>
            </div>
          </div>

          {/* Right (슬라이드 유지) */}
          <div className="hidden lg:block">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 shadow-2xl">

              <div className="bg-white/95 rounded-xl p-3 overflow-hidden">
                <img
                  src={slides[currentSlide].image}
                  className="w-full rounded-lg"
                />
              </div>

              <div className="mt-5">
                <h3 className="text-white text-xl font-semibold">
                  {slides[currentSlide].title}
                </h3>
                <p className="text-white/70 text-sm mt-2">
                  {slides[currentSlide].description}
                </p>
              </div>

              {/* 컨트롤 */}
              <div className="mt-5 flex items-center justify-between">
                <div className="flex gap-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      className={`rounded-full ${
                        currentSlide === i
                          ? "w-8 h-2 bg-white"
                          : "w-2 h-2 bg-white/40"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={prevSlide}
                    className="w-10 h-10 rounded-full bg-white/10 text-white"
                  >
                    ‹
                  </button>
                  <button
                    onClick={nextSlide}
                    className="w-10 h-10 rounded-full bg-white/10 text-white"
                  >
                    ›
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}